import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
  AuthenticationDetails } from 'amazon-cognito-identity-js'
import router from '@/router'

const poolData = {
  UserPoolId: process.env.AWS_COGNITO_USER_POOL_ID,
  ClientId: process.env.AWS_COGNITO_CLIENT_ID
}

const userPool = new CognitoUserPool(poolData)

const getCognitoUser = function (username) {
  const userData = {
    Username: username,
    Pool: userPool
  }

  return new CognitoUser(userData)
}

const getCognitoUserSession = function (currentUser) {
  const sessionData = {
    IdToken: new CognitoIdToken({IdToken: currentUser.idToken}),
    AccessToken: new CognitoAccessToken({AccessToken: currentUser.accessToken}),
    RefreshToken: new CognitoRefreshToken({RefreshToken: currentUser.refreshToken})
  }
  return new CognitoUserSession(sessionData)
}

let logoutTimer = null

const state = {
  autoLogout: {
    isAutoLogout: false,
    salutation: null,
    firstName: null,
    lastName: null
  }
}

const getters = {
  autoLogout: state => state.autoLogout
}

const mutations = {
  setAutoLogout (state, autoLogout) {
    state.autoLogout = autoLogout
  }
}

const actions = {
  setLogoutTimer ({commit, getters, dispatch}) {
    if (logoutTimer) {
      clearTimeout(logoutTimer)
    }
    logoutTimer = setTimeout(() => {
      dispatch('signOut').then(() => {
        commit('setAutoLogout', {
          isAutoLogout: true,
          salutation: getters.currentUser.salutation,
          firstName: getters.currentUser.firstName,
          lastName: getters.currentUser.lastName
        })
        commit('setCurrentUser', {})
        router.push('/signin')
      })
    }, 20 * 1000 * 60)
  },
  changePassword ({getters, dispatch, commit}, data) {
    const currentUser = getters.currentUser
    const cognitoUser = getCognitoUser(currentUser.email)
    cognitoUser.setSignInUserSession(getCognitoUserSession(currentUser))
    return new Promise((resolve, reject) => {
      cognitoUser.changePassword(data.oldPassword, data.newPassword, (err, result) => {
        if (!err) {
          dispatch('changedPassword')
          resolve()
        } else {
          reject(err)
        }
      })
    })
  },
  confirmResetPassword ({commit}, data) {
    const cognitoUser = getCognitoUser(data.email)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(data.verificationCode, data.password, {
        onSuccess () {
          resolve()
        },
        onFailure (err) {
          reject(err)
        }
      })
    })
  },
  confirmSignUp ({commit}, data) {
    const cognitoUser = getCognitoUser(data.email)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(data.verificationCode, true, (err, result) => {
        if (err) {
          reject(err)
        }
        resolve()
      })
    })
  },
  deleteCognitoUser ({getters, dispatch, commit}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const cognitoUser = getCognitoUser(currentUser.email)
      cognitoUser.setSignInUserSession(getCognitoUserSession(currentUser))
      if (cognitoUser) {
        // Workaround because Cognito will call the callback twice
        let callbackCounter = 0
        cognitoUser.deleteUser((err, result) => {
          if (!err && callbackCounter === 0) {
            callbackCounter++
            dispatch('deleteCurrentUser')
              .then(() => {
                commit('setCurrentUser', {})
                resolve()
              })
              .catch((err) => reject(err))
          } else if (err) {
            reject(err)
          } else {
            resolve()
          }
        })
      } else {
        reject('User is not signed in')
      }
    })
  },
  resendConfirmSignUpCode ({commit}, email) {
    const cognitoUser = getCognitoUser(email)
    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err)
        }
        resolve()
      })
    })
  },
  resetPassword ({commit}, email) {
    const cognitoUser = getCognitoUser(email)
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess (data) {
          resolve()
        },
        onFailure (error) {
          reject(error)
        }
      })
    })
  },
  signIn ({dispatch, getters, commit}, authData) {
    const authenticationData = {
      Username: authData.email,
      Password: authData.password
    }
    const authDetails = new AuthenticationDetails(authenticationData)
    const cognitoUser = getCognitoUser(authenticationData.Username)
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess (session) {
          const cognitoId = cognitoUser.username
          const idToken = session.idToken.getJwtToken()
          const accessToken = session.accessToken.getJwtToken()
          const refreshToken = session.refreshToken.token
          const expirationDate = new Date(session.idToken.payload.exp * 1000 - (30 * 60000))
          localStorage.setItem('cognitoId', cognitoId)
          localStorage.setItem('idToken', idToken)
          localStorage.setItem('accessToken', accessToken)
          localStorage.setItem('refreshToken', refreshToken)
          localStorage.setItem('expirationDate', expirationDate)
          const invitation = getters.invitation
          if (Object.keys(invitation).length > 0) {
            const contactPersonRelation = {
              idToken,
              invitationUuid: invitation.uuid,
              relationshipType: invitation.relation
            }
            dispatch('createContactPersonRelation', contactPersonRelation)
              .then(() => {
                return dispatch('setCurrentUser', {
                  cognitoId,
                  idToken,
                  accessToken,
                  refreshToken,
                  expirationDate
                })
              })
              .then(() => {
                commit('setInvitation', {})
                resolve()
              })
              .catch((err) => reject(err))
          } else {
            dispatch('setCurrentUser', {
              cognitoId,
              idToken,
              accessToken,
              refreshToken,
              expirationDate
            }).then(() => {
              resolve()
            }).catch((err) => reject(err))
          }
        },
        onFailure (error) {
          reject(error)
        }
      })
    })
  },
  tryAutoLogin ({dispatch}) {
    return new Promise((resolve, reject) => {
      const cognitoId = localStorage.getItem('cognitoId')
      const accessToken = localStorage.getItem('accessToken')
      const refreshToken = localStorage.getItem('refreshToken')
      const idToken = localStorage.getItem('idToken')
      const expirationDate = localStorage.getItem('expirationDate')
      if (!idToken) {
        reject()
      } else {
        dispatch('setCurrentUser', {
          cognitoId,
          idToken,
          accessToken,
          refreshToken,
          expirationDate
        }).then(() => resolve())
          .catch(() => reject())
      }
    })
  },
  signOut ({getters}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      if (currentUser) {
        const cognitoUser = getCognitoUser(currentUser.email)
        cognitoUser.setSignInUserSession(getCognitoUserSession(currentUser))
        cognitoUser.signOut()
        localStorage.clear()
        resolve()
      } else {
        reject()
      }
    })
  },
  signUp ({dispatch, commit}, regData) {
    return new Promise((resolve, reject) => {
      const attributes = []
      Object.keys(regData).map(key => {
        switch (key) {
          case 'email':
            attributes.push(new CognitoUserAttribute({ Name: 'email', Value: regData['email'] }))
            break
          case 'firstName':
            attributes.push(new CognitoUserAttribute({ Name: 'given_name', Value: regData['firstName'] }))
            break
          case 'lastName':
            attributes.push(new CognitoUserAttribute({ Name: 'name', Value: regData['lastName'] }))
            break
          case 'salutation':
            attributes.push(new CognitoUserAttribute({ Name: 'custom:salutation', Value: regData['salutation'] }))
            break
          case 'dateOfBirth':
            attributes.push(new CognitoUserAttribute({ Name: 'birthdate', Value: regData['dateOfBirth'] }))
            break
          case 'contractNumber':
            attributes.push(new CognitoUserAttribute({ Name: 'custom:contractNumber', Value: regData['contractNumber'] }))
            break
          case 'roles':
            let roles = regData['roles']
            attributes.push(new CognitoUserAttribute({ Name: 'custom:role', Value: roles[0].roleOfUser }))
            break
          case 'relation':
            attributes.push(new CognitoUserAttribute({ Name: 'custom:relationship', Value: regData['relation'] }))
            break
          case 'invitationUuid':
            attributes.push(new CognitoUserAttribute({ Name: 'custom:invitationUuid', Value: regData['invitationUuid'] }))
            break
          case 'invitationEmail':
            attributes.push(new CognitoUserAttribute({ Name: 'custom:invitationEmail', Value: regData['invitationEmail'] }))
            break
          case 'invitationInviterId':
            attributes.push(new CognitoUserAttribute({ Name: 'custom:invitationInviterId', Value: regData['invitationInviterId'].toString() }))
            break
          default:
            break
        }
      })

      if (regData.invitationUuid) {
        userPool.signUp(regData.email, regData.password, attributes, null, (err, data) => {
          if (!err) {
            commit('setInvitation', {})
            resolve()
          }
          reject(err)
        })
      } else {
        dispatch('validateContractNumber', regData)
          .then(() => {
            userPool.signUp(regData.email, regData.password, attributes, null, (err, data) => {
              if (!err) {
                resolve()
              }
              reject(err)
            })
          })
          .catch((err) => {
            reject(err.response.data)
          })
      }
    })
  },
  updateAttributes ({commit, dispatch, getters}, attributes) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const cognitoUser = getCognitoUser(currentUser.email)
      cognitoUser.setSignInUserSession(getCognitoUserSession(currentUser))
      const cognitoUserAttributes = attributes.map(attr => new CognitoUserAttribute(attr))
      cognitoUser.updateAttributes(cognitoUserAttributes, (err, result) => {
        if (!err) {
          dispatch('refreshSession')
            .then(() => resolve())
            .catch(() => reject())
        } else {
          reject()
        }
      })
    })
  },
  refreshSession ({commit, getters}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const cognitoUser = getCognitoUser(currentUser.email)
      cognitoUser.setSignInUserSession(getCognitoUserSession(currentUser))
      cognitoUser.refreshSession(cognitoUser.signInUserSession.refreshToken, (err, success) => {
        if (!err) {
          currentUser.idToken = success.idToken.getJwtToken()
          currentUser.accessToken = success.accessToken.getJwtToken()
          currentUser.refreshToken = success.refreshToken.token
          currentUser.expirationDate = new Date(success.idToken.payload.exp * 1000 - (30 * 60000))
          commit('setCurrentUser', currentUser)
          resolve()
        } else {
          reject()
        }
      })
    })
  }
}

export default {
  actions,
  state,
  mutations,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/auth.js